import store from './store'
import { signOutEverywhere, signOutHere } from './signout'

// One entry in the menu. Either a place to go or a control to press, never both
// and never neither -- `to` and `action` are mutually exclusive by construction
// here rather than by a type that would only say it in a comment, because a
// `v-list-item` given a `to` navigates and cannot also be pressed.
export interface UserMenuItem {
  label: string

  // The route this entry leads to, if it leads anywhere.
  to?: string

  // What pressing this entry does, if pressing it does anything.
  action?: () => void | Promise<void>
}

export interface UserMenu {
  // The username rather than the name or the email address, deliberately: an
  // account has two identifiers and this is the one that has to be remembered
  // when signing in from somewhere new, so it is the one worth showing. The
  // display name and the email address are both on the profile screen (ticket
  // 12), which is where a person goes to read them.
  username: string | null

  items: UserMenuItem[]
}

// What the menu offers, decided from the store rather than from anything the
// component was told.
//
// This exists as a module and not as a computed property inside `App.vue` because
// that is the only part of the user menu with a decision in it, and the decision
// is worth having under a test: which affordances appear is a function of the
// session and the admin flag alone, so it can be read off without mounting
// anything. The component renders whatever this returns.
//
// The two sign-out controls are both here, and both are labelled for what they
// do rather than for where they are. "Sign out" is the client-side one a person
// reaches for on a shared machine; "Sign out everywhere" is the one that asks the
// server to revoke every session the account holds, and it says so because the
// difference is the whole reason the two are separate -- see `signout.ts`.
export function userMenu (): UserMenu {
  if (!store.getters.signedIn) {
    return { username: null, items: [{ label: 'Sign in', to: '/signin' }] }
  }

  const items: UserMenuItem[] = [
    // Where the user's own lists are. In the menu rather than in the navigation
    // drawer beside "Listas", because it is the only listing route in the
    // application that needs a session -- showing it to a visitor with no session
    // would be offering a page that answers with a redirect to the sign-in form.
    { label: 'My lists', to: '/lists' },
    { label: 'My profile', to: '/profile' },
    { label: 'Sign out', action: signOutHere }
  ]

  // The one administrative affordance, offered on the strength of the flag the
  // sign-in response carried. `getters.admin` rather than the flag read off the
  // token, which does not carry it -- see `store.ts` and `guards.ts`. A stale
  // flag gets an admin a link to a screen whose calls the server answers `403`,
  // which is the honest failure; a missing one gets a non-admin no link at all.
  if (store.getters.admin) {
    items.unshift({ label: 'Manage users', to: '/admin/users' })
  }

  items.push({ label: 'Sign out everywhere', action: signOutEverywhere })

  return { username: store.state.user?.username || null, items }
}
